import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Users, CheckCircle, Megaphone, ClipboardList, MapPin, Heart } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

const ROLES = [
  { id: "polling", label: "Polling Booth Worker", desc: "Help voters at the booth on election day", icon: MapPin },
  { id: "handing_out", label: "Handing Out How-to-Votes", desc: "Distribute party material at polling stations", icon: Megaphone },
  { id: "data_entry", label: "Campaign Data Entry", desc: "Assist with voter records and campaign logistics", icon: ClipboardList },
  { id: "doorknocking", label: "Doorknocking", desc: "Talk to voters in your local area", icon: Users },
  { id: "events", label: "Campaign Events", desc: "Help organise rallies, fundraisers, and community events", icon: Heart },
  { id: "social_media", label: "Social Media", desc: "Create and share content for your preferred party", icon: Megaphone },
];

const PARTY_LINKS = [
  { name: "Labor (ALP)", url: "https://www.alp.org.au/volunteer", color: "#CC2529" },
  { name: "Liberal Party", url: "https://www.liberal.org.au/volunteer", color: "#1B4F9E" },
  { name: "The Greens", url: "https://greens.org.au/volunteer", color: "#10B981" },
  { name: "The Nationals", url: "https://nationals.org.au/volunteer", color: "#155E3B" },
];

export default function VolunteerPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [form, setForm] = useState({ name: "", email: "", state: "", interests: [], message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  const toggleInterest = (id) => {
    setForm((prev) => ({
      ...prev,
      interests: prev.interests.includes(id)
        ? prev.interests.filter((i) => i !== id)
        : [...prev.interests, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.state) {
      setError("Please fill in your name, email, and state.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer`, form);
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="volunteer-page">
      {/* Hero */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Get Involved
          </h1>
          <p className="text-sm max-w-xl" style={{ color: "#94A3B8" }}>
            Democracy works because ordinary people show up. Whether it's handing out how-to-votes,
            doorknocking, or working a booth — your time makes a difference.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Left column */}
          <div>
            {/* Role types */}
            <h2
              className="font-black text-xl mb-4"
              style={{ fontFamily: "Outfit, sans-serif", color: text }}
            >
              Ways to Volunteer
            </h2>
            <div className="space-y-3 mb-8">
              {ROLES.map((role) => {
                const Icon = role.icon;
                return (
                  <div
                    key={role.id}
                    className="border-2 border-black p-3 flex items-start gap-3"
                    style={{ backgroundColor: surface }}
                  >
                    <div
                      className="w-8 h-8 border-2 border-black flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "#2E5948" }}
                    >
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-black text-sm" style={{ color: text }}>{role.label}</p>
                      <p className="text-xs" style={{ color: textSec }}>{role.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Party volunteer links */}
            <h2
              className="font-black text-xl mb-4"
              style={{ fontFamily: "Outfit, sans-serif", color: text }}
            >
              Volunteer Directly with a Party
            </h2>
            <div className="space-y-2">
              {PARTY_LINKS.map((p) => (
                <a
                  key={p.name}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between border-2 border-black px-4 py-3 font-bold text-sm hover:-translate-y-0.5 transition-transform"
                  style={{ backgroundColor: p.color, color: "#fff", boxShadow: "2px 2px 0 #000" }}
                  data-testid={`party-volunteer-link-${p.name.split(" ")[0].toLowerCase()}`}
                >
                  {p.name}
                  <span className="text-xs opacity-75">volunteer.{p.name.split(" ")[0].toLowerCase()}.org →</span>
                </a>
              ))}
            </div>
          </div>

          {/* Right column — Sign Up Form */}
          <div>
            <h2
              className="font-black text-xl mb-4"
              style={{ fontFamily: "Outfit, sans-serif", color: text }}
            >
              Register Your Interest
            </h2>

            {submitted ? (
              <div
                className="border-2 border-black p-8 text-center"
                style={{ backgroundColor: "#10B98120", borderColor: "#10B981", boxShadow: "4px 4px 0 #10B981" }}
                data-testid="volunteer-success"
              >
                <CheckCircle className="w-10 h-10 mx-auto mb-3" style={{ color: "#10B981" }} />
                <p className="font-black text-lg mb-2" style={{ color: text }}>You're registered!</p>
                <p className="text-sm" style={{ color: textSec }}>
                  Thanks for putting your hand up. We'll be in touch with opportunities in your area.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="border-2 border-black p-6 space-y-4"
                style={{ backgroundColor: surface, boxShadow: "4px 4px 0 #000" }}
                data-testid="volunteer-form"
              >
                <div>
                  <label className="block text-xs font-black mb-1 uppercase tracking-wide" style={{ color: textSec }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    required
                    data-testid="volunteer-name-input"
                    className="w-full border-2 border-black px-3 py-2 text-sm font-medium focus:outline-none"
                    style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-black mb-1 uppercase tracking-wide" style={{ color: textSec }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    data-testid="volunteer-email-input"
                    className="w-full border-2 border-black px-3 py-2 text-sm font-medium focus:outline-none"
                    style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-black mb-1 uppercase tracking-wide" style={{ color: textSec }}>
                    State / Territory *
                  </label>
                  <select
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    required
                    data-testid="volunteer-state-select"
                    className="w-full border-2 border-black px-3 py-2 text-sm font-medium focus:outline-none"
                    style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text }}
                  >
                    <option value="">Select state...</option>
                    {STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black mb-2 uppercase tracking-wide" style={{ color: textSec }}>
                    I'm interested in (select all that apply)
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {ROLES.map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => toggleInterest(role.id)}
                        className="text-xs px-2 py-1 border-2 border-black font-bold transition-all"
                        style={{
                          backgroundColor: form.interests.includes(role.id) ? "#2E5948" : "transparent",
                          color: form.interests.includes(role.id) ? "#fff" : text,
                        }}
                      >
                        {role.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black mb-1 uppercase tracking-wide" style={{ color: textSec }}>
                    Additional message (optional)
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Anything else you'd like us to know..."
                    rows={3}
                    data-testid="volunteer-message-input"
                    className="w-full border-2 border-black px-3 py-2 text-sm font-medium focus:outline-none resize-none"
                    style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text }}
                  />
                </div>
                {error && (
                  <p className="text-xs font-bold" style={{ color: "#C04E3F" }} data-testid="volunteer-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="volunteer-submit-btn"
                  className="w-full py-3 font-black text-sm border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
                  style={{ backgroundColor: "#2E5948", color: "#fff", boxShadow: "2px 2px 0 #000" }}
                >
                  {loading ? "Submitting..." : "Register to Volunteer"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
